<template>
    <Head :title="$t('apartments.notes')" />

    <div class="p-2 md:p-6">
        <div class="mb-6">
            <h1 class="text-2xl font-semibold text-minplan-blue">
                {{ $t("apartments.notes") }}
            </h1>
            <p class="text-sm text-minplan-darkbeige mt-1">
                {{ $t("apartments.notesBuyerSubtitle") }}
            </p>
        </div>

        <div v-if="entries.length">
            <template v-for="(entry, i) in entries" :key="entry.uuid">
                <p
                    v-if="monthLabel(i)"
                    class="text-xs uppercase tracking-wider text-minplan-darkbeige mt-6 mb-2"
                >
                    {{ monthLabel(i) }}
                </p>

                <div
                    class="bg-white border border-minplan-lightwhite rounded-lg mb-3 hover:border-gray-200"
                >
                    <button
                        type="button"
                        class="w-full flex items-start gap-4 p-4 text-left"
                        @click="toggle(entry.uuid)"
                    >
                        <span
                            class="flex-none w-10 h-10 rounded-lg bg-minplan-lightwhite grid place-items-center text-minplan-greybeige"
                        >
                            <component :is="iconFor(entry.category)" class="h-5 w-5" />
                        </span>

                        <span class="flex-1 min-w-0">
                            <span class="block font-semibold text-minplan-blue truncate">
                                {{ entry.title }}
                            </span>
                            <span
                                class="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-minplan-darkbeige"
                            >
                                <span>{{
                                    $t("apartments.noteCategories." + entry.category)
                                }}</span>
                                <span>{{ entry.dateFormatted }}</span>
                                <span>{{ entry.author }}</span>
                            </span>
                        </span>

                        <ChevronDownIcon
                            class="flex-none h-5 w-5 text-gray-300 transition-transform"
                            :class="{ 'rotate-180': isOpen(entry.uuid) }"
                        />
                    </button>

                    <!-- Keine Teilnehmenden, keine Fusszeile "erfasst am …"
                         (Entscheide 5 und 6). Der Server liefert beides
                         käuferseitig gar nicht erst aus — hier steht deshalb
                         auch nichts, was man ausblenden müsste. -->
                    <div v-if="isOpen(entry.uuid)" class="px-4 pb-4 pl-18">
                        <div class="border-t border-minplan-lightwhite pt-3 ml-14">
                            <p
                                v-if="entry.body"
                                class="text-sm text-minplan-darkgreybeige whitespace-pre-wrap leading-relaxed"
                            >
                                {{ entry.body }}
                            </p>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <p v-else class="py-10 text-center text-sm text-minplan-darkgreybeige">
            {{ $t("apartments.emptyNotes") }}
        </p>
    </div>
</template>

<script>
import { defineComponent } from "vue";
import { Head } from "@inertiajs/inertia-vue3";
import { ChevronDownIcon } from "@heroicons/vue/outline";
import { noteIconFor } from "@/Shared/noteIcons";
import { noteMonthLabel } from "@/Shared/noteMonths";
import ClientApartmentLayout from "@/Layouts/ClientApartmentLayout";

/**
 * Die Notizen-Seite des Käufers (Auftrag Luca, 2026-08-17, Entscheid 3).
 *
 * Bewusst eine eigene Seite statt einer geteilten mit
 * `Pages/Apartments/Notes.vue`: dort trägt jede Karte Bedienelemente
 * (Bearbeiten, Löschen, Freigabe-Schalter), die es hier nicht gibt, und die
 * Seite hält den ganzen Formular-Zustand. Geteilt wird, was wirklich gleich
 * ist — das Symbol je Kategorie und die Monats-Trennzeile.
 *
 * `notes` fehlt, solange keine Notiz der Wohnung freigegeben ist (Entscheid 7:
 * HTTP 200 mit Leermeldung, kein 404).
 */
export default defineComponent({
    props: ["apartment", "notes"],
    layout: ClientApartmentLayout,
    components: {
        Head,
        ChevronDownIcon,
    },
    data() {
        return {
            open: [],
        };
    },
    computed: {
        entries() {
            return this.notes || [];
        },
    },
    methods: {
        iconFor(category) {
            return noteIconFor(category);
        },
        monthLabel(index) {
            return noteMonthLabel(this.$t.bind(this), this.entries, index);
        },
        isOpen(uuid) {
            return this.open.includes(uuid);
        },
        toggle(uuid) {
            const i = this.open.indexOf(uuid);
            if (i === -1) this.open.push(uuid);
            else this.open.splice(i, 1);
        },
    },
});
</script>

<style scoped></style>
